import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import "./verifyEmail.css";
import { resetSuccess } from "../CreateAccount/slice";
import { loginUser } from "../Login/slice";
import VerificationCodeInput from "../../components/WebApp/VerificationCodeInput";
import { verifyEmail, resendVerificationCode } from "./slice";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [timeLeft, setTimeLeft] = useState(60);
  const [isResending, setIsResending] = useState(true);
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  const { userEmail } = useSelector((state) => state.createAccount);
  const { codeError, success } = useSelector((state) => state.verifyEmail);

  const emailToVerify =
    userEmail || sessionStorage.getItem("verificationEmail");

  // Determine where the user came from
  const cameFromLogin =
    !userEmail && sessionStorage.getItem("verificationEmail");

  useEffect(() => {
    // Initial setup
    if (!emailToVerify) {
      navigate("/create-account");
      return;
    }

    if (userEmail) {
      sessionStorage.setItem("verificationEmail", userEmail);
    }

    // Cleanup function
    return () => {
      // Only cleanup on unmount if verification was successful
      if (success) {
        sessionStorage.removeItem("verificationEmail");
      }
    };
  }, [emailToVerify, navigate, userEmail, success]);

  useEffect(() => {
    if (timeLeft > 0 && isResending) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setIsResending(false);
      setTimeLeft(60);
    }
  }, [timeLeft, isResending]);

  const handleCodeComplete = (code) => {
    setVerificationCode(code);
  };

  const handleVerifyClick = () => {
    if (verificationCode) {
      setIsVerifying(true);
      dispatch(verifyEmail({ email: emailToVerify, code: verificationCode }))
        .then((result) => {
          if (verifyEmail.fulfilled.match(result)) {
            // If user came from login page, redirect back to login after verification
            if (cameFromLogin) {
              sessionStorage.removeItem("verificationEmail");
              navigate("/login");
              return;
            }

            // If user came from registration, proceed with auto-login
            return dispatch(
              loginUser({
                email: emailToVerify,
                password: sessionStorage.getItem("tempPassword"),
              })
            );
          }
          setIsVerifying(false);
        })
        .then((loginResult) => {
          if (loginResult && loginUser.fulfilled.match(loginResult)) {
            sessionStorage.removeItem("verificationEmail");
            sessionStorage.removeItem("tempPassword");
            navigate("/register-success");
          } else if (loginResult !== undefined) {
            setIsVerifying(false);
          }
        })
        .catch(() => {
          setIsVerifying(false);
        });
    }
  };

  const handleResend = () => {
    setIsResending(true);
    dispatch(resendVerificationCode(emailToVerify));
  };

  const handleBack = () => {
    sessionStorage.removeItem("verificationEmail");
    if (!cameFromLogin) {
      dispatch(resetSuccess());
    }
    // Use navigate(-1) to go back to the previous page in history
    navigate(-1);
  };

  return (
    <div className="verify-email-container">
      <div className="back-button">
        <button onClick={handleBack} className="back-link-button">
          ← Back
        </button>
      </div>

      <div className="verify-email-content">
        <h1>Verify Email</h1>

        <p className="email-sent-message">
          A 6-digit code has been sent to
          <br />
          <span className="user-email">{emailToVerify}</span>
        </p>

        <p className="verification-instruction">
          Please enter the verification code below
        </p>

        <VerificationCodeInput
          onComplete={handleCodeComplete}
          error={codeError}
          isLoading={isVerifying}
          actionButtonText="Verify Email"
          showResend={true}
          onResend={handleResend}
          isResending={isResending}
          timeLeft={timeLeft}
          onSubmit={handleVerifyClick}
        />

        <div className="login-link-container">
          Already have an account?{" "}
          <Link
            to="/login"
            className="text-button-link"
            onClick={() => {
              sessionStorage.removeItem("verificationEmail");
              if (!cameFromLogin) {
                dispatch(resetSuccess());
              }
            }}
          >
            Log In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
